/**
 * M83 agreements — PLATFORM-operator service.
 *
 * Every call here requires a live platform grant and must NEVER be reached from a
 * vendor/customer surface. The subject surface lives in `api.ts`.
 *
 * Transport paths carry NO `/api/v1` prefix: the base URL already includes it.
 */
import { z } from 'zod';
import { http, generateIdempotencyKey } from '@/api/client/http';
import { ResponseMetaSchema } from '@/api/schemas/common';
import {
  AgreementDocumentListResponseSchema,
  AgreementDocumentResponseSchema,
  AgreementEvidencePackageSchema,
  AgreementExecutionListResponseSchema,
  AgreementRequirementResponseSchema,
  AgreementVersionListResponseSchema,
} from '@/api/schemas/agreements-platform';
import {
  AgreementExecutionDetailResponseSchema,
  AgreementVersionResponseSchema,
} from '@/api/schemas/agreements';
import type {
  AgreementDocumentListResponse,
  AgreementDocumentResponse,
  AgreementEvidencePackage,
  AgreementExecutionListResponse,
  AgreementRequirementResponse,
  AgreementVersionListResponse,
  CreateAgreementDocumentRequest,
  CreateAgreementRequirementRequest,
  PublishAgreementVersionRequest,
  SetAgreementDocumentStatusRequest,
  WithdrawAgreementVersionRequest,
} from '@/api/schemas/agreements-platform';
import type {
  AgreementExecutionDetailResponse,
  AgreementVersionResponse,
} from '@/api/schemas/agreements.types';

function envelope<T extends z.ZodTypeAny>(schema: T) {
  return z.object({ data: schema, meta: ResponseMetaSchema });
}

/** Query filters the platform execution ledger accepts. */
export type PlatformExecutionQuery = {
  limit?: number;
  cursor?: string;
  status?: string;
  subjectKind?: string;
  subjectId?: string;
  documentId?: string;
};

export const agreementsPlatformApi = {
  /* ------------------------------------------------------------------------------------------
   * Documents
   * ---------------------------------------------------------------------------------------- */

  createDocument: (input: CreateAgreementDocumentRequest) =>
    http
      .post<{ data: AgreementDocumentResponse }>(
        '/platform/agreement-documents',
        envelope(AgreementDocumentResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  listDocuments: () =>
    http
      .get<{ data: AgreementDocumentListResponse }>(
        '/platform/agreement-documents',
        envelope(AgreementDocumentListResponseSchema),
      )
      .then((r) => r.data),

  getDocument: (documentId: string) =>
    http
      .get<{ data: AgreementDocumentResponse }>(
        `/platform/agreement-documents/${documentId}`,
        envelope(AgreementDocumentResponseSchema),
      )
      .then((r) => r.data),

  setDocumentStatus: (documentId: string, input: SetAgreementDocumentStatusRequest) =>
    http
      .post<{ data: AgreementDocumentResponse }>(
        `/platform/agreement-documents/${documentId}/status`,
        envelope(AgreementDocumentResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /* ------------------------------------------------------------------------------------------
   * Versions
   * ---------------------------------------------------------------------------------------- */

  /**
   * The document's version history (§2.16, added in register v1.6).
   *
   * A read collection on the SAME path the publish command appends to. The rows arrive in the
   * server's `version DESC, locale ASC` order and are passed through untouched: re-sorting here
   * would make two consumers disagree about which version is "first". Withdrawn versions are
   * included — they are part of the record. An absent document is a 404, not an empty list.
   */
  listVersions: (documentId: string) =>
    http
      .get<{ data: AgreementVersionListResponse }>(
        `/platform/agreement-documents/${documentId}/versions`,
        envelope(AgreementVersionListResponseSchema),
      )
      .then((r) => r.data),

  publishVersion: (documentId: string, input: PublishAgreementVersionRequest) =>
    http
      .post<{ data: AgreementVersionResponse }>(
        `/platform/agreement-documents/${documentId}/versions`,
        envelope(AgreementVersionResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  withdrawVersion: (versionId: string, input: WithdrawAgreementVersionRequest) =>
    http
      .post<{ data: AgreementVersionResponse }>(
        `/platform/agreement-versions/${versionId}/withdrawal`,
        envelope(AgreementVersionResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /* ------------------------------------------------------------------------------------------
   * Requirements
   * ---------------------------------------------------------------------------------------- */

  createRequirement: (input: CreateAgreementRequirementRequest) =>
    http
      .post<{ data: AgreementRequirementResponse }>(
        '/platform/agreement-requirements',
        envelope(AgreementRequirementResponseSchema),
        { body: input, idempotencyKey: generateIdempotencyKey() },
      )
      .then((r) => r.data),

  /* ------------------------------------------------------------------------------------------
   * Execution ledger + restricted evidence
   * ---------------------------------------------------------------------------------------- */

  listExecutions: (query: PlatformExecutionQuery = {}) =>
    http
      .get<{ data: AgreementExecutionListResponse }>(
        '/platform/agreement-executions',
        envelope(AgreementExecutionListResponseSchema),
        { query: { ...query } },
      )
      .then((r) => r.data),

  getExecution: (executionId: string) =>
    http
      .get<{ data: AgreementExecutionDetailResponse }>(
        `/platform/agreement-executions/${executionId}`,
        envelope(AgreementExecutionDetailResponseSchema),
      )
      .then((r) => r.data),

  /**
   * The RESTRICTED evidence package.
   *
   * A SEPARATE route under a SEPARATE, narrower grant (`platform.agreement.evidence.read`):
   * reading the deployment-wide ledger does not imply reading a subject's IP, device and
   * location facts. The UI must treat this as restricted and never surface it to a subject.
   */
  exportEvidence: (executionId: string) =>
    http
      .get<{ data: AgreementEvidencePackage }>(
        `/platform/agreement-executions/${executionId}/evidence`,
        envelope(AgreementEvidencePackageSchema),
      )
      .then((r) => r.data),
};
